import moment from "moment";
import React, { useMemo, useState } from "react";
import { Badge, Button, Pagination, Table } from "flowbite-react";
import { FaArrowsLeftRight } from "react-icons/fa6";
import axios from "../../config/axiosInstance";
import { useQuery, useQueryClient } from "react-query";
import { ADMIN, CATEGORIES, LEADS } from "../../config/apiConfig";
import { IoMdAdd, IoMdSearch } from "react-icons/io";
import { FaFileInvoiceDollar, FaRegEdit } from "react-icons/fa";
import EmptyTable from "./EmptyTable";
import { MdBlock } from "react-icons/md";
import ModalDelete from "../modals/ModalDelete";
import TableLoading from "../TableLoading";
import { Tooltip } from "react-tooltip";
import CompanyInvoiceForm from "../forms/CompanyInvoiceForm";
import { formatDocument } from "../../utils/utils";
import QuickCreateCustomer from "../forms/QuickCreateCustomer";

/**
 * AdminCustomersTable – Versão aprimorada
 * - Topbar com título, contador e CTA primário
 * - Busca destacada com ícone + filtros de status
 * - Cards no mobile (bonitos) e tabela no desktop (header sticky + zebra)
 * - Ações com tooltips: Editar, Fatura, Bloquear/Desbloquear
 * - Mantém React Query e componentes existentes
 */

const STATUS_MAP = {
  1: { label: "Ativo", badge: "green" },
  0: { label: "Bloqueado", badge: "failure" },
  2: { label: "Inadimplente", badge: "warning" },
};

const STATUS_FILTERS = [
  { key: "all", label: "Todos" },
  { key: "1", label: "Ativos" },
  { key: "0", label: "Bloqueados" },
  { key: "2", label: "Inadimplentes" },
];

const AdminCustomersTable = () => {
  const queryClient = useQueryClient();

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const onPageChange = (page) => setCurrentPage(page);

  const [searchName, setSearchName] = useState("");
  const [nameParam, setNameParam] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isOpenModalBlock, setOpenModalBlock] = useState(false);
  const [isLoadingBlock, setIsLoadingBlock] = useState(false);
  const [typeBlock, setTypeBlock] = useState(0);

  const [isOpenQuickCreate, setIsOpenQuickCreate] = useState(false);
  const [isDrawerEditCustomer, setIsDrawerEditCustomer] = useState(false);
  const [isDrawerViewInvoice, setIsDrawerViewInvoice] = useState(false);

  // === Data ===
  const {
    isLoading: isLoadingListCustomers,
    error: errorListCustomers,
    data: listCustomers,
  } = useQuery(["listCustomers", currentPage, nameParam], async () => {
    const { data } = await axios.get(
      `${LEADS.LIST}?page=${currentPage}&search=${nameParam}`
    );
    setTotalPages(data?.data?.totalPages || 1);
    setTotalCount(data?.data?.totalCount || 0);
    return data?.data?.data || [];
  });

  // Caso use em outra parte (mantido como no original)
  useQuery(["categories"], async () => {
    const { data } = await axios.get(
      `${CATEGORIES.GET_ALL}?itemsPerPage=${-1}`
    );
    return data?.data?.data || [];
  });

  // === Handlers ===
  const handleSearchName = (e) => {
    e.preventDefault();
    setNameParam(searchName.trim());
    setCurrentPage(1);
  };

  const handleViewInvoice = (company) => {
    setSelectedCompany(company);
    setIsDrawerViewInvoice(true);
  };

  const handleEditCustomer = (company) => {
    setSelectedCompany(company);
    setIsDrawerEditCustomer(true); // usa QuickCreateCustomer modo "edit"
  };

  const handleBlockCustomer = (company, type) => {
    setSelectedCompany(company);
    setTypeBlock(type);
    setOpenModalBlock(true);
  };

  const blockUnlockCustomer = async (type = 0) => {
    if (!selectedCompany?.id) return;
    try {
      setIsLoadingBlock(true);
      await axios.put(`${ADMIN.GET_COMPANY}/${selectedCompany.id}`, {
        status: type,
      });
      queryClient.invalidateQueries(["listCustomers", currentPage, nameParam]);
      setOpenModalBlock(false);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingBlock(false);
    }
  };

  // === Memo: Filtragem de status no cliente (opcional, além do search do backend) ===
  const filteredCustomers = useMemo(() => {
    if (!listCustomers) return [];
    if (statusFilter === "all") return listCustomers;
    return listCustomers.filter((c) => String(c.status) === statusFilter);
  }, [listCustomers, statusFilter]);

  // === UI helpers ===
  const renderStatusBadge = (status) => {
    const meta = STATUS_MAP[status] || { label: "-", badge: "gray" };
    return (
      <Badge color={meta.badge} className="mx-auto w-fit text-[11px]">
        {meta.label}
      </Badge>
    );
  };

  const blockUnblockButton = (customer) => {
    const isActive = customer.status === 1;
    return (
      <button
        data-tooltip-id="tt-block"
        onClick={() => handleBlockCustomer(customer, isActive ? 0 : 1)}
        className={`p-2 rounded-lg border bg-white transition ${
          isActive
            ? "border-red-200 text-red-600 hover:bg-red-50"
            : "border-green-200 text-green-600 hover:bg-green-50"
        }`}
        aria-label={isActive ? "Bloquear" : "Desbloquear"}
      >
        <MdBlock size={16} />
      </button>
    );
  };

  return (
    <>
      {/* ======= Topbar ======= */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-4">
        <div className="flex-1">
          <h2 className="text-xl md:text-2xl font-semibold tracking-tight">
            Clientes
          </h2>
          <p className="text-sm text-gray-500">
            Gerencie cadastro, permissões e faturas dos seus clientes.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="px-3 py-1.5 rounded-md bg-gray-100 text-gray-700 text-sm">
            Total: <span className="font-medium">{totalCount}</span>
          </div>
          <Button
            className="bg-primary text-white flex items-center"
            onClick={() => setIsOpenQuickCreate(true)}
          >
            <span>Adicionar cliente</span>
          </Button>
        </div>
      </div>

      {/* ======= Search + Filters ======= */}
      <form
        onSubmit={handleSearchName}
        className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 mb-4"
      >
        <div className="flex-1">
          <div className="relative">
            <IoMdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              placeholder="Busque por nome, telefone ou e-mail..."
              className="w-full pl-10 pr-3 py-2 rounded-md border border-gray-200 focus:border-gray-300 focus:ring-0 text-sm"
              onChange={(e) => setSearchName(e.target.value)}
              value={searchName}
            />
          </div>
        </div>

        <div className="flex gap-1.5 overflow-x-auto">
          {STATUS_FILTERS.map((f) => {
            const active = statusFilter === f.key;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setStatusFilter(f.key)}
                className={`px-3 py-2 rounded-md border text-sm whitespace-nowrap transition ${
                  active
                    ? "bg-gray-900 text-white border-gray-900"
                    : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        <div className="flex md:w-auto">
          <Button
            size="sm"
            className="bg-secondary w-full md:w-auto"
            type="submit"
          >
            Buscar
          </Button>
        </div>
      </form>

      {/* ======= Hint swipe mobile ======= */}
      {(filteredCustomers?.length > 0 || isLoadingListCustomers) && (
        <small
          className="gap-2 items-center mb-2 flex md:hidden justify-center text-gray-500"
          style={{ fontSize: 10 }}
        >
          <span>Deslize para ver mais informações</span> <FaArrowsLeftRight />
        </small>
      )}

      {/* ======= Mobile Cards ======= */}
      <div className="grid grid-cols-1 gap-3 md:hidden">
        {!isLoadingListCustomers ? (
          filteredCustomers?.length > 0 ? (
            filteredCustomers.map((customer, idx) => (
              <div
                key={idx}
                className="rounded-xl border border-[#e9e4df] bg-white shadow-sm p-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-gray-900 font-medium leading-tight truncate">
                      {customer.name || customer.socialReason}
                    </div>
                    <div className="text-[12px] text-gray-500">
                      CNPJ: {formatDocument(customer.document || "")}
                    </div>
                  </div>
                  {renderStatusBadge(customer.status)}
                </div>

                <div className="mt-2 grid grid-cols-2 gap-2 text-[12px]">
                  <div className="bg-gray-50 rounded-md p-2">
                    <div className="text-gray-500">Cadastro</div>
                    <div className="font-medium">
                      {moment(customer.createdAt).format("DD/MM/YYYY")}
                    </div>
                    <div className="text-[11px] text-gray-500 -mt-0.5">
                      {moment(customer.createdAt).fromNow()}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-md p-2">
                    <div className="text-gray-500">Representante</div>
                    <div
                      className="font-medium truncate"
                      title={
                        customer?.ParentCompany?.name ||
                        customer?.ParentCompany?.socialReason ||
                        "-"
                      }
                    >
                      {customer?.ParentCompany?.name ||
                        customer?.ParentCompany?.socialReason || (
                          <span className="text-gray-400">-</span>
                        )}
                    </div>
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-end gap-2">
                  <Tooltip id="tt-edit" content="Editar cliente" />
                  <button
                    data-tooltip-id="tt-edit"
                    onClick={() => handleEditCustomer(customer)}
                    className="p-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition"
                    aria-label="Editar"
                  >
                    <FaRegEdit size={16} />
                  </button>

                  <Tooltip id="tt-block" content="Bloquear/Desbloquear" />
                  {blockUnblockButton(customer)}
                </div>
              </div>
            ))
          ) : (
            <EmptyTable icon="person_off">Nenhum cliente encontrado</EmptyTable>
          )
        ) : (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="animate-pulse rounded-xl border border-gray-200 p-3"
              >
                <div className="h-4 bg-gray-100 rounded w-2/3 mb-2" />
                <div className="h-3 bg-gray-100 rounded w-1/2 mb-1" />
                <div className="h-3 bg-gray-100 rounded w-1/3" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ======= Desktop Table ======= */}
      <div className="hidden md:block mt-0">
        <div className="overflow-x-auto">
          <div className="min-w-full rounded-xl border border-[#e9e4df] bg-white shadow-sm overflow-hidden">
            <Table striped={false} className="text-xs xl:text-sm">
              <Table.Head className="uppercase text-[11px] tracking-wide sticky top-0 z-5">
                <Table.HeadCell className="bg-gray-300 text-red-800 font-semibold">
                  Cliente
                </Table.HeadCell>
                <Table.HeadCell className="text-center bg-gray-300 text-red-800 font-semibold">
                  Data cadastro
                </Table.HeadCell>
                <Table.HeadCell className="text-center bg-gray-300 text-red-800 font-semibold">
                  Status
                </Table.HeadCell>
                <Table.HeadCell className="text-center bg-gray-300 text-red-800 font-semibold">
                  Ações
                </Table.HeadCell>
              </Table.Head>

              <Table.Body className="divide-y divide-gray-100">
                {!isLoadingListCustomers ? (
                  filteredCustomers?.length > 0 ? (
                    filteredCustomers.map((customer, index) => (
                      <Table.Row
                        key={index}
                        className={`transition ${
                          index % 2 === 0 ? "bg-white" : "bg-gray-50"
                        } hover:bg-[#faf7f4]`}
                      >
                        {/* Cliente */}
                        <Table.Cell className="align-middle py-3">
                          <div className="text-gray-900 font-medium leading-tight truncate">
                            {customer.name || customer.socialReason}
                          </div>
                          <div className="text-[11px] text-gray-500 mt-0.5">
                            CNPJ: {formatDocument(customer.document || "")}
                          </div>
                        </Table.Cell>

                        {/* Data cadastro */}
                        <Table.Cell className="align-middle text-center py-3">
                          <div className="text-gray-900 font-medium">
                            {moment(customer.createdAt).format("DD/MM/YYYY")}
                          </div>
                          <div className="text-[11px] text-gray-500 -mt-0.5">
                            {moment(customer.createdAt).fromNow()}
                          </div>
                        </Table.Cell>

                        {/* Status */}
                        <Table.Cell className="align-middle text-center py-3">
                          {renderStatusBadge(customer.status)}
                        </Table.Cell>

                        {/* Ações */}
                        <Table.Cell className="align-middle text-center py-2">
                          <div className="flex items-center justify-center gap-2">
                            <Tooltip id="tt-edit" content="Editar cliente" />
                            <button
                              data-tooltip-id="tt-edit"
                              onClick={() => handleEditCustomer(customer)}
                              className="p-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition"
                              aria-label="Editar"
                            >
                              <FaRegEdit size={16} />
                            </button>
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    ))
                  ) : (
                    <Table.Row>
                      <Table.Cell
                        colSpan={5}
                        className="text-center py-8 text-gray-500"
                      >
                        Nenhum cliente encontrado.
                      </Table.Cell>
                    </Table.Row>
                  )
                ) : (
                  <div className="space-y-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div
                        key={i}
                        className="animate-pulse rounded-xl border border-gray-200 p-3"
                      >
                        <div className="h-4 bg-gray-100 rounded w-2/3 mb-2" />
                        <div className="h-3 bg-gray-100 rounded w-1/2 mb-1" />
                        <div className="h-3 bg-gray-100 rounded w-1/3" />
                      </div>
                    ))}
                  </div>
                )}
              </Table.Body>
            </Table>
          </div>
        </div>
      </div>

      {/* ======= Total & Pagination ======= */}
      {totalCount > 0 && (
        <div className="mt-3 text-center text-sm">
          Foram encontrados {totalCount} registros no total
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex overflow-x-auto justify-center mt-3">
          <Pagination
            layout="pagination"
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            previousLabel=""
            nextLabel=""
            showIcons
          />
        </div>
      )}

      {/* ======= Modais/Drawers ======= */}
      {/* Create */}
      <QuickCreateCustomer
        show={isOpenQuickCreate}
        onClose={() => setIsOpenQuickCreate(false)}
        onUpdated={() => {
          queryClient.invalidateQueries([
            "listCustomers",
            currentPage,
            nameParam,
          ]);
        }}
      />

      {/* Edit (reaproveitando o mesmo componente em modo edição) */}
      <QuickCreateCustomer
        show={isDrawerEditCustomer}
        onClose={() => setIsDrawerEditCustomer(false)}
        mode="edit"
        initialCompany={selectedCompany}
        onUpdated={() => {
          queryClient.invalidateQueries([
            "listCustomers",
            currentPage,
            nameParam,
          ]);
        }}
      />

      {/* Faturas (opcional: substitua por seu Drawer/Modal de faturas) */}
      {isDrawerViewInvoice && (
        <CompanyInvoiceForm
          show={isDrawerViewInvoice}
          onClose={() => setIsDrawerViewInvoice(false)}
          company={selectedCompany}
        />
      )}

      {/* Bloquear/Desbloquear */}
      <ModalDelete
        title={`Deseja ${
          typeBlock === 0 ? "bloquear" : "desbloquear"
        } este cliente?`}
        isOpen={isOpenModalBlock}
        setOpenModal={setOpenModalBlock}
        isLoading={isLoadingBlock}
        handleDelete={() => blockUnlockCustomer(typeBlock)}
      />
    </>
  );
};

export default AdminCustomersTable;
